import type { AnalyticsGranularity } from '@/api/types';
import { TOGGLE_STYLES, TOGGLE_BUTTON_BASE } from '@/styles/toggleStyles';

interface GranularityToggleProps {
    /** Currently selected granularity */
    value: AnalyticsGranularity;
    /** Callback when granularity changes */
    onChange: (granularity: AnalyticsGranularity) => void;
    /** Disable yearly option (e.g., for short time periods) */
    disableYearly?: boolean;
    /** Disable monthly option (e.g., for future projections) */
    disableMonthly?: boolean;
    /** Disable all options */
    disabled?: boolean;
}

const options: { label: string; value: AnalyticsGranularity; ariaLabel: string }[] = [
    { label: 'M', value: 'month', ariaLabel: 'Monthly view' },
    { label: 'Y', value: 'year', ariaLabel: 'Yearly view' },
];

/**
 * Toggle component for switching between monthly and yearly granularity.
 * Compact design with fixed-width buttons (M/Y labels).
 * Styled as a button group following the TypeFilter pattern.
 */
export function GranularityToggle({ value, onChange, disableYearly = false, disableMonthly = false, disabled = false }: GranularityToggleProps) {
    return (
        <div className={`${TOGGLE_STYLES.container} ${disabled ? 'opacity-50' : ''}`}>
            {options.map((option) => {
                const isActive = value === option.value;
                const isOptionDisabled = disabled || (disableYearly && option.value === 'year') || (disableMonthly && option.value === 'month');
                const disabledTitle = disabled
                    ? 'Not enough data'
                    : disableYearly && option.value === 'year'
                    ? 'Yearly view not available for periods under 1 year'
                    : disableMonthly && option.value === 'month'
                    ? 'Monthly view not available for future projections'
                    : undefined;
                return (
                    <button
                        key={option.value}
                        type="button"
                        onClick={() => !isOptionDisabled && onChange(option.value)}
                        disabled={isOptionDisabled}
                        title={disabledTitle}
                        className={`
                            w-10 py-1.5 text-sm font-medium ${TOGGLE_BUTTON_BASE}
                            ${isActive ? TOGGLE_STYLES.active : TOGGLE_STYLES.inactive}
                            ${isOptionDisabled ? 'opacity-40 cursor-not-allowed' : ''}
                        `}
                        aria-pressed={isActive}
                        aria-label={option.ariaLabel}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}
